'use client'

import * as React from 'react'
import { cn } from 'app/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { ComboButtonView } from './combo-button'
import { ComboOptionsView } from './combo-options'
import { GroupedComboOptionsView } from './grouped-combo-options'
import { Tooltip } from 'app/components/Tooltip'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

export type ComboboxProps = {
  name?: string
  options?: OptionType[]
  placeholder?: string
  className?: string
  inputClassName?: string
  optionsContainerClassName?: string
  optionClassName?: string
  chevronClassName?: string
  customIcon?: React.ReactNode
  customButton?: React.ReactNode | ((isOpen: boolean) => React.ReactNode)
  customButtonClassName?: string
  customLabel?: string
  defaultValue?: OptionType
  value?: OptionType
  onChange?: (val: OptionType) => void
  onChangeMultiple?: (val: OptionType[]) => void
  onSearchValueChange?: (text: string) => void
  onClearSearch?: () => void
  onButtonClick?: () => void
  loading?: boolean
  open?: boolean
  showSearchInDropdown?: boolean
  disabled?: boolean
  multiple?: boolean
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  selected?: OptionType[]
  onAddNewValue?: (val: string) => void
  forceOpen?: boolean
  checkboxSide?: `left` | `right`
  showButtonTooltip?: boolean
  allowDeselectOption?: boolean
  useSingleFieldInput?: boolean
  isFilter?: boolean
  placement?: `bottom-start` | `bottom-end` | `top-start` | `top-end`
  /**
   * Forwarded to Radix `PopoverContent`. Defaults to `true` (Radix default) so
   * existing consumers are unaffected. Pass `false` to pin the dropdown to the
   * requested `placement` and prevent it from flipping upward when the trigger
   * sits low in the viewport (e.g. inside a tall modal).
   */
  avoidCollisions?: boolean
  addText?: string
  isOptionsGrouped?: boolean
  popoverStyle?: React.CSSProperties
}

const NOOP = () => {}

const PLACEMENT_MAP = {
  'bottom-start': { side: 'bottom' as const, align: 'start' as const },
  'bottom-end': { side: 'bottom' as const, align: 'end' as const },
  'top-start': { side: 'top' as const, align: 'start' as const },
  'top-end': { side: 'top' as const, align: 'end' as const },
}

export function ComboBoxView({
  name,
  options,
  placeholder,
  defaultValue,
  value,
  className,
  inputClassName,
  optionsContainerClassName,
  optionClassName,
  chevronClassName,
  onSearchValueChange,
  customIcon,
  customButton,
  customButtonClassName,
  customLabel,
  loading = false,
  open: initialOpen = false,
  showSearchInDropdown = false,
  disabled = false,
  onChange = NOOP,
  onClearSearch,
  onButtonClick,
  multiple = false,
  enableDeselectAllOption = false,
  deselectAllOptions,
  selected,
  onChangeMultiple,
  onAddNewValue,
  forceOpen,
  checkboxSide,
  showButtonTooltip = false,
  allowDeselectOption,
  useSingleFieldInput = true,
  isFilter,
  placement = `bottom-start`,
  avoidCollisions = true,
  addText = `Add new value`,
  isOptionsGrouped = false,
  popoverStyle,
}: ComboboxProps) {
  const isMobile = typeof window !== `undefined` && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  const [isOpen, setIsOpen] = useState(initialOpen)
  const [tooltipContent, setTooltipContent] = useState<string | undefined>(undefined)
  const triggerRef = useRef<HTMLDivElement>(null)
  const resolvedCustomButton = typeof customButton === `function` ? customButton(isOpen) : customButton

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      onButtonClick?.()
      if (isMobile && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        window.scrollTo({ top: rect.top + scrollTop - 10, behavior: `smooth` })
      }
    }
  }

  const handleAddNewValue = onAddNewValue
    ? (val: string) => {
        setIsOpen(false)
        onAddNewValue(val)
      }
    : undefined

  const handleSingleSelect = (option: OptionType) => {
    onChange(option)
    setIsOpen(false)
    onClearSearch?.()
  }

  const handleMultiSelect = (option: OptionType) => {
    if (!onChangeMultiple || !selected) return
    const deselectAll = deselectAllOptions?.[0]
    const isDeselectAll = deselectAll && option.value === deselectAll.value
    const wasDeselectAllSelected = deselectAll && selected.some((s) => s.value === deselectAll.value)

    if (isDeselectAll && !wasDeselectAllSelected) {
      onChangeMultiple([option])
    } else if (isDeselectAll && wasDeselectAllSelected) {
      // clicking deselect-all when already selected — do nothing or deselect
      onChangeMultiple(selected.filter((s) => s.value !== deselectAll.value))
    } else if (wasDeselectAllSelected) {
      // selecting a regular option when deselect-all was selected
      const opts = [...selected.filter((s) => s.value !== deselectAll.value)]
      const exists = opts.some((s) => s.id === option.id)
      onChangeMultiple(exists ? opts.filter((s) => s.id !== option.id) : [...opts, option])
    } else {
      const exists = selected.some((s) => s.id === option.id)
      onChangeMultiple(exists ? selected.filter((s) => s.id !== option.id) : [...selected, option])
    }
  }

  useEffect(() => {
    if (initialOpen) setIsOpen(true)
  }, [initialOpen])

  useEffect(() => {
    debug(`contacts`, `ComboBoxView`, name, { value })
  }, [value])

  const { side, align } = PLACEMENT_MAP[placement] || PLACEMENT_MAP['bottom-start']

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {!multiple && useSingleFieldInput && <input type="hidden" name={name} value={value?.value} />}
      <div className={cn(className, `relative h-full`, disabled && `cursor-not-allowed`, isOpen && `z-50`)}>
        <PopoverTrigger asChild disabled={disabled}>
          {resolvedCustomButton ? (
            <div ref={triggerRef} className={customButtonClassName} data-testid="combo-button-1">
              {resolvedCustomButton}
            </div>
          ) : (
            <ComboButtonView
              ref={triggerRef}
              inputClassName={inputClassName}
              customIcon={customIcon}
              chevronClassName={cn(chevronClassName, multiple && disabled && `d-none`)}
              disabled={disabled}
              isOpen={isOpen}
            >
              {multiple ? (
                <Tooltip
                  placement="top"
                  message={selected && selected.length > 1 ? selected?.map((opt) => opt.name).join(`, `) : undefined}
                  className={cn(
                    `overflow-hidden`,
                    disabled ? `!cursor-not-allowed text-minimal opacity-70` : `!cursor-pointer`,
                    isFilter && selected && selected.length === 1 ? `!w-32` : ``,
                  )}
                >
                  <div className={cn(`truncate`, !isFilter && `mt-1`)}>
                    {customLabel
                      ? customLabel
                      : selected && selected.length > 1
                        ? `${selected.length} selected`
                        : selected && selected.length === 1
                          ? selected[0].name
                          : (placeholder ?? `Search`)}
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  placement="top"
                  className={cn(
                    `truncate pr-2`,
                    disabled ? `!cursor-not-allowed opacity-70` : `!cursor-pointer`,
                    !inputClassName && (disabled ? `text-body-sm text-minimal` : `text-body-sm text-strong`),
                  )}
                  message={showButtonTooltip ? tooltipContent : undefined}
                  onMouseOver={(event) => {
                    if (!showButtonTooltip) return
                    if (
                      event.currentTarget instanceof HTMLElement &&
                      event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                    ) {
                      setTooltipContent(value?.name)
                    } else {
                      setTooltipContent(undefined)
                    }
                  }}
                >
                  <span
                    data-testid="combo-value"
                    className={cn(
                      disabled
                        ? `cursor-not-allowed text-minimal opacity-70`
                        : !value?.name
                          ? `cursor-pointer text-minimal`
                          : `cursor-pointer text-strong`,
                    )}
                  >
                    {value?.meta?.icon && (
                      <img
                        alt={value.meta.iconAlt ?? `option icon`}
                        className="mr-2 inline h-auto w-4 align-middle"
                        src={value.meta.icon}
                      />
                    )}
                    {value?.name ?? placeholder ?? `Search`}
                  </span>
                </Tooltip>
              )}
            </ComboButtonView>
          )}
        </PopoverTrigger>

        <PopoverContent
          side={side}
          align={align}
          avoidCollisions={avoidCollisions}
          sideOffset={5}
          className={cn(
            `z-50 w-auto overflow-hidden rounded-lg border-default bg-surface-raised p-0 shadow-elevation-4`,
            optionsContainerClassName,
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{
            // Collapsing a group changes the widest row, so a content-sized popover would
            // resize on every toggle.
            ...(isOptionsGrouped ? { width: `var(--radix-popover-trigger-width)` } : null),
            minWidth: isOptionsGrouped ? `320px` : `var(--radix-popover-trigger-width)`,
            maxWidth: `620px`,
            ...popoverStyle,
          }}
        >
          {/*
            Grouped rendering has two implementations, forked here:
            - single-select grouped → GroupedComboOptionsView (its own grouping/collapse logic)
            - multi-select grouped → ComboOptionsView with `isOptionsGrouped` (checkbox rows + collapse)
            If these ever need to unify, this is the seam.
          */}
          {isOptionsGrouped && !multiple ? (
            <GroupedComboOptionsView
              open={isOpen}
              options={options}
              showSearchInDropdown={showSearchInDropdown}
              value={value}
              onClearSearch={onClearSearch}
              onSelect={handleSingleSelect}
            />
          ) : (
            <ComboOptionsView
              open={isOpen}
              forceOpen={forceOpen}
              optionsContainerClassName={optionsContainerClassName}
              showSearchInDropdown={multiple || showSearchInDropdown}
              onSearchValueChange={onSearchValueChange}
              onClearSearch={onClearSearch}
              loading={loading}
              options={options}
              optionClassName={optionClassName}
              enableDeselectAllOption={enableDeselectAllOption}
              deselectAllOptions={deselectAllOptions}
              multiple={multiple}
              selected={selected}
              value={value}
              onSelect={multiple ? handleMultiSelect : handleSingleSelect}
              onAddNewValue={handleAddNewValue}
              checkboxSide={checkboxSide}
              allowDeselectOption={allowDeselectOption}
              addText={addText}
              isOptionsGrouped={isOptionsGrouped}
            />
          )}
        </PopoverContent>
      </div>
    </Popover>
  )
}
